<template>
  <div>
    <v-dialog v-model="open" max-width="500px" persistent>
      <v-card class="pa-4">
        <v-toolbar elevation="0">
          <v-card-title class="pl-0">
            {{ title }}
          </v-card-title>

          <v-spacer />

          <v-btn icon @click="open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4 py-0">
          <v-form ref="form" v-model="formValid" lazy-validation class="pa-0 ma-0">
            <v-row>
              <v-col cols="12" md="12" sm="12" class="pb-0">
                <v-text-field :readonly="!canUpdateTag" label="Nome" v-model="formData.name" :rules="rules" required
                  outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="12" sm="12" class="pt-0">
                <v-select :readonly="!canUpdateTag" v-model="formData.account_id" :error="accountError"
                  :items="accounts.data" :loading="loadingAccounts" item-text="account_name" item-value="id"
                  menu-props="auto" label="Conta" hide-details single-line outlined :rules="rulesAccount"
                  color="primary"></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="close">
            Cancelar
          </v-btn>
          <confirm-modal v-if="actionType === 'EDIT' && canDeleteTag" :loading="loadingDelete" text action-color="error"
            action-text="Excluir" :action="deleteKeyword" title="Excluir tag"
            description="Tem certeza que deseja excluir a tag?" />
          <v-btn v-if="canUpdateTag" :loading="loading" color="primary" text @click="save">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { handleResponseError } from '@/api/handleError'
import ConfirmModal from '@/components/ConfirmModal'
import { mapState } from 'vuex'
import * as permissionsNames from '@/constants/permissions'
import hasPermission from '@/helpers/hasPermission'

export default {
  name: 'KeywordDetailModal',
  props: {
    actionType: String,
    data: {
      type: Object,
      required: true,
    },
    open: Boolean,
    title: {
      type: String,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    onDelete: {
      type: Function,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  components: {
    ConfirmModal,
  },
  computed: {
    ...mapState({
      accounts: state => state.accounts.all,
    }),
    canDeleteTag() {
      return hasPermission(permissionsNames.CAN_DELETE_TAGS)
    },
    canUpdateTag() {
      return hasPermission(permissionsNames.CAN_UPDATE_TAGS)
    },
  },
  methods: {
    resetData: function () {
      this.formData.name = this.formDataOriginal.name
      this.formData.account_id = this.formDataOriginal.account_id
    },
    close: function () {
      this.resetData()
      this.onClose()
    },
    save: async function () {
      if (this.$refs.form.validate()) {
        this.loading = true
        if (this.actionType === 'CREATE') {
          await this.$store.dispatch('keywords/create', this.formData)
            .then(() => {
              this.resetData()
              this.loading = false
              this.onSave()
            })
            .catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao criar tag.')
              this.showNotification(message, 'red darken-2')
              this.loading = false
            })
        } else {
          await this.$store.dispatch('keywords/update', { id: this.data.id, data: this.formData })
            .then(() => {
              this.resetData()
              this.loading = false
              this.onSave()
            }).catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao salvar a tag.')
              this.showNotification(message, 'red darken-2')
              this.loading = false
            })
        }
      }
    },
    deleteKeyword: async function () {
      this.loadingDelete = true
      await this.$store.dispatch('keywords/delete', this.data.id).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao excluir a tag.')
        this.showNotification(message, 'red darken-2')
        this.loadingDelete = false
      })
      this.loadingDelete = false
      this.onDelete()
    },
    showNotification: function (message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    fetchAccounts: async function () {
      this.loadingAccounts = true
      this.accountError = false
      await this.$store.dispatch('accounts/getAccounts', { limit: 0 }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as contas.')
        this.showNotification(message, 'red darken-2')
        this.loadingAccounts = false
        this.accountError = true
      })
      this.loadingAccounts = false
      this.accountError = false
    },
  },
  mounted() {
    this.formData.name = this.data.name
    this.formData.account_id = this.data.account_id
    this.formDataOriginal.name = this.data.name
    this.formDataOriginal.account_id = this.data.account_id
    this.fetchAccounts()
  },
  data: () => ({
    loadingAccounts: false,
    accountError: false,
    loading: false,
    formValid: false,
    loadingDelete: false,
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    formData: {
      name: '',
      account_id: null,
    },
    formDataOriginal: {
      name: '',
      account_id: null,
    },
    rules: [
      v => !!v || 'O nome é obrigatório',
    ],
    rulesAccount: [
      v => !!v || 'A conta é obrigatória',
    ],
  }),
  watch: {
    'data.name': function (name) {
      this.formData.name = name
      this.formDataOriginal.name = name
    },
    'data.account_id': function (account_id) {
      this.formData.account_id = account_id
      this.formDataOriginal.account_id = account_id
    },
  },
}
</script>

<style scoped></style>